import React, { useEffect, useState, createContext, useContext } from 'react';
import { Check, X, PackagePlus } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface ToastProps {
  message: string;
  type?: 'success' | 'error' | 'info';
  duration?: number;
  onClose: () => void;
}

export function Toast({ message, type = 'success', duration = 4000, onClose }: ToastProps) {
  useEffect(() => {
    const timer = setTimeout(onClose, duration);
    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <Check size={20} className="text-green-500" />;
      case 'error':
        return <X size={20} className="text-red-500" />;
      default:
        return <PackagePlus size={20} className="text-blue-500" />;
    }
  };

  const getBgColor = () => {
    switch (type) {
      case 'success':
        return 'border-green-500/30 bg-green-500/10';
      case 'error':
        return 'border-red-500/30 bg-red-500/10';
      default:
        return 'border-blue-500/30 bg-blue-500/10';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 50, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 20, scale: 0.95 }}
      className={`flex items-center gap-3 p-4 rounded-lg border backdrop-blur-sm ${getBgColor()}`}
    >
      {getIcon()}
      <span className="text-white flex-1">{message}</span>
      <button
        onClick={onClose}
        className="text-white/60 hover:text-white transition-colors"
      >
        <X size={16} />
      </button>
    </motion.div>
  );
}

interface ToastManagerProps {
  children: React.ReactNode;
}

interface ToastData {
  id: string;
  message: string;
  type: 'success' | 'error' | 'info';
}

// Toast Context
interface ToastContextType {
  addToast: (message: string, type?: 'success' | 'error' | 'info') => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export function useToast(): ToastContextType {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastManager');
  }
  return context;
}

export function ToastManager({ children }: ToastManagerProps) {
  const [toasts, setToasts] = useState<ToastData[]>([]);

  useEffect(() => {
    const handleCompleteSetAdded = (event: CustomEvent) => {
      const { setName, finish, cardCount } = event.detail;
      addToast(`Successfully added ${cardCount} cards from ${setName} (${finish}) to your collection!`, 'success');
    };

    const handleCompleteSetRemoved = (event: CustomEvent) => {
      const { setName, finish } = event.detail;
      addToast(`Successfully removed ${setName} (${finish}) from your collection!`, 'success');
    };

    document.addEventListener('completeSetAdded', handleCompleteSetAdded as EventListener);
    document.addEventListener('completeSetRemoved', handleCompleteSetRemoved as EventListener);
    return () => {
      document.removeEventListener('completeSetAdded', handleCompleteSetAdded as EventListener);
      document.removeEventListener('completeSetRemoved', handleCompleteSetRemoved as EventListener);
    };
  }, []);

  const addToast = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
    const id = Date.now().toString();
    setToasts(prev => [...prev, { id, message, type }]);
  };

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  return (
    <ToastContext.Provider value={{ addToast }}>
      {children}
      
      {/* Toast Container */}
      <div className="fixed bottom-4 right-4 z-50 space-y-2 max-w-md">
        <AnimatePresence>
          {toasts.map(toast => (
            <Toast
              key={toast.id}
              message={toast.message}
              type={toast.type}
              onClose={() => removeToast(toast.id)}
            />
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
} 